import { useCallback, useReducer, useRef } from "react";

import { readAccessToken, readActiveMembershipId } from "./auth";

export interface UserMsg {
  readonly role: "user";
  readonly text: string;
}

export interface AssistantMsg {
  readonly role: "assistant";
  readonly text: string;
}

export type ChatMsg = UserMsg | AssistantMsg;

interface State {
  threadId: string | null;
  messages: ChatMsg[];
  pendingText: string;
  activeTools: string[];
  streaming: boolean;
  error: string | null;
  pendingNavigation: string | null;
}

type Action =
  | { type: "user_sent"; text: string }
  | { type: "delta"; text: string }
  | { type: "tool_start"; name: string }
  | { type: "tool_end"; name: string }
  | { type: "done"; thread_id: string }
  | { type: "error"; detail: string }
  | { type: "navigate"; route: string }
  | { type: "navigation_consumed" }
  | { type: "aborted" };

const INITIAL: State = {
  threadId: null,
  messages: [],
  pendingText: "",
  activeTools: [],
  streaming: false,
  error: null,
  pendingNavigation: null,
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "user_sent":
      return {
        ...state,
        messages: [...state.messages, { role: "user", text: action.text }],
        pendingText: "",
        activeTools: [],
        streaming: true,
        error: null,
        pendingNavigation: null,
      };
    case "delta":
      return { ...state, pendingText: state.pendingText + action.text };
    case "tool_start":
      return { ...state, activeTools: [...state.activeTools, action.name] };
    case "tool_end":
      return {
        ...state,
        activeTools: state.activeTools.filter((n) => n !== action.name),
      };
    case "done": {
      const assistantText = state.pendingText.trim();
      const next: ChatMsg[] = assistantText
        ? [...state.messages, { role: "assistant", text: assistantText }]
        : state.messages;
      return {
        ...state,
        threadId: action.thread_id,
        messages: next,
        pendingText: "",
        activeTools: [],
        streaming: false,
      };
    }
    case "error":
      return {
        ...state,
        pendingText: "",
        activeTools: [],
        streaming: false,
        error: action.detail,
      };
    case "navigate":
      return { ...state, pendingNavigation: action.route };
    case "navigation_consumed":
      return { ...state, pendingNavigation: null };
    case "aborted":
      return { ...state, pendingText: "", activeTools: [], streaming: false };
    default:
      return state;
  }
}

export interface UseChatStreamResult {
  messages: ChatMsg[];
  pendingText: string;
  activeTools: string[];
  streaming: boolean;
  error: string | null;
  pendingNavigation: string | null;
  consumeNavigation: () => void;
  send: (text: string, locale: string) => void;
  cancel: () => void;
}

export function useChatStream(chatUrl: string | undefined): UseChatStreamResult {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const abortRef = useRef<AbortController | null>(null);
  const threadIdRef = useRef<string | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: "aborted" });
  }, []);

  const consumeNavigation = useCallback(() => {
    dispatch({ type: "navigation_consumed" });
  }, []);

  const send = useCallback(
    (text: string, locale: string) => {
      if (!chatUrl || state.streaming) return;

      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      dispatch({ type: "user_sent", text });

      const userMsg = {
        role: "user",
        content: [{ type: "text", text }],
      };

      if (!threadIdRef.current) {
        threadIdRef.current = crypto.randomUUID();
      }

      const body = JSON.stringify({
        thread_id: threadIdRef.current,
        locale,
        messages: [
          ...state.messages.map((m) => ({
            role: m.role,
            content: [{ type: "text", text: m.text }],
          })),
          userMsg,
        ],
      });

      void (async () => {
        try {
          const token = readAccessToken();
          const membershipId = readActiveMembershipId();
          const headers: Record<string, string> = {
            "Content-Type": "application/json",
          };
          if (token) headers["Authorization"] = `Bearer ${token}`;
          if (membershipId) headers["X-Active-Membership-Id"] = membershipId;

          const resp = await fetch(chatUrl, {
            method: "POST",
            headers,
            body,
            signal: ctrl.signal,
          });

          if (!resp.ok || !resp.body) {
            dispatch({ type: "error", detail: `HTTP ${resp.status}` });
            return;
          }

          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buf = "";

          while (true) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const lines = buf.split("\n");
            buf = lines.pop() ?? "";

            for (const line of lines) {
              if (!line.startsWith("data: ")) continue;
              const raw = line.slice(6).trim();
              if (!raw) continue;
              let evt: Record<string, unknown>;
              try {
                evt = JSON.parse(raw) as Record<string, unknown>;
              } catch {
                continue;
              }
              const kind = evt["type"] as string | undefined;
              if (kind === "delta") {
                dispatch({ type: "delta", text: String(evt["text"] ?? "") });
              } else if (kind === "tool_call") {
                dispatch({ type: "tool_start", name: String(evt["name"] ?? "") });
              } else if (kind === "tool_result") {
                dispatch({ type: "tool_end", name: String(evt["name"] ?? "") });
              } else if (kind === "done") {
                const tid = String(evt["thread_id"] ?? threadIdRef.current ?? "");
                threadIdRef.current = tid;
                dispatch({ type: "done", thread_id: tid });
              } else if (kind === "navigate") {
                dispatch({ type: "navigate", route: String(evt["route"] ?? "") });
              } else if (kind === "error") {
                const code = String(evt["code"] ?? "error");
                dispatch({ type: "error", detail: code });
              }
            }
          }
        } catch (err: unknown) {
          if (err instanceof DOMException && err.name === "AbortError") return;
          dispatch({ type: "error", detail: "stream_error" });
        }
      })();
    },
    [chatUrl, state.messages, state.streaming],
  );

  return {
    messages: state.messages,
    pendingText: state.pendingText,
    activeTools: state.activeTools,
    streaming: state.streaming,
    error: state.error,
    pendingNavigation: state.pendingNavigation,
    consumeNavigation,
    send,
    cancel,
  };
}
